import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { Box, Grid, Typography, styled } from '@mui/material';
import { COACH_ROUTES } from 'constants/app';
import { SectionMain } from 'layouts/ScreenLayout/sections/SectionMain';
import { MuiTableComponent } from 'screens/common/CustomUI';
import { getMemberDetailsNew, refreshMemberList } from 'store/actions/member';
import { formatDate, formatDateTime, getAge, parseName, timeFormat } from 'utilities/Utilities';
import BasicBreadcrumbs from 'components/Shared/BreadCrumb/BreadCrumb';
import RefreshIcon from 'assets/images/icons/refresh-header.svg';
import { GENDERS, STATUS } from 'constants/session';
import { programMap } from 'utilities/Constants';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getEKGAlgorithmDeterminationDisplayText } from 'constants/recordingMaps';
import { getDateFilterOperators } from 'screens/common/DateFilter/CustomDateFilter';

/**
 * Fetching the member list data based on admin and coach
 * This component is Specific to Coach and Admin
 * It displays the list of members with their details
 * and allows navigation to member profile on click of member name.
 * It also provides a refresh functionality to update the member list.
 * @param {Function} props.getMemberDetails - Function to fetch member details.
 * @returns {JSX.Element} - Rendered MemberList component.
 */

export default function MemberList(props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { getMemberDetails, memberList, count } = props;

  // =======Component level state ========//
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredRow, setFilteredRow] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const [totalRowCount, setTotalRowCount] = useState(0);

  //======== Data from Redux ======//
  const coachId = useSelector((state) => state.user.profile.id);
  const { isAdmin } = useSelector((state) => state.user.profile.permissions);
  const refreshTime = useSelector((state) => state.memberList?.refreshTime);
  const userCountry = useSelector((state) => state.user.profile.country);

  //==== Styled Components for Table Row Text ===//
  const TableRowText = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    fontFamily: 'Work Sans',
  }));

  //==== Initial Set of the data for Member List Table===//
  const [apiQueryParams, setapiQueryParams] = useState({
    pageLimit: pageSize,
    page: 1,
    sortColumn: 'user_last_name',
    sortType: 'ASC',
    filter: '',
    coachId: coachId,
    isAdmin: isAdmin,
  });

  // Setting up the data
  useEffect(() => {
    setapiQueryParams((prev) => ({
      ...prev,
      page: page + 1,
      pageLimit: pageSize,
    }));
  }, [page, pageSize]);

  // Fetching the member list data on component mount and when apiQueryParams or refreshTime changes
  useEffect(() => {
    tableAPICall(apiQueryParams);
  }, [apiQueryParams, refreshTime]);

  // Fetching the member list data on component mount
  const tableAPICall = (queryParams = null) => {
    getMemberDetailsNew(queryParams).then((res) => {
      setFilteredRow(
        res?.participantList?.map((row) => ({
          ...row,
          name: parseName(row.participantFirstName, row.participantLastName),
          coach: parseName(row?.coachFirstName, row?.coachLastName),
        })),
      );
      setTotalRowCount(res?.totalCount);
    });
  };

  // ===== This calculates Last Login column ======//
  const computeLastLogin = (val) => {
    let oneDayBefore = moment().subtract(1, 'days');
    let oneWeekBefore = moment().subtract(7, 'days');
    if (val) {
      switch (true) {
        case moment(val) > oneDayBefore:
          return moment(val).format('hh:mm A');
        case moment(val) < oneWeekBefore:
          return moment(val).format('MM-DD-YYYY');
        default:
          return moment(val).fromNow();
      }
    }
  };

  //==== NAVIGATION TO MEMBER PROFILE SCREEN ON CLICK OF MEMBER COLUMN DATA ====//
  const memberProfileNavigation = (value, e) => {
    history.push(`${COACH_ROUTES.MEMBER_PROFILE_BASE}/${value}`);
  };

  //==== Column Data for Member List Table ===//
  const columnData = [
    {
      field: 'name',
      headerName: 'Name',
      width: 250,
      editable: false,
      filterable: true,
      type: 'string',
      renderCell: (params) => (
        <TableRowText
          variant="body2"
          onClick={() => memberProfileNavigation(params?.row.participantId)}
        >
          {params?.value || '--'}
        </TableRowText>
      ),
    },
    ...(isAdmin
      ? [
          {
            field: 'coach',
            headerName: 'Coach',
            width: 180,
            type: 'string',
            renderCell: (params) => (
              <TableRowText variant="body2">{params?.value || '--'}</TableRowText>
            ),
          },
        ]
      : []),

    {
      field: 'sponsor',
      headerName: 'Sponsor',
      width: 100,
      editable: false,
      filterable: true,
      type: 'string',
      valueGetter: (params) => params?.row?.sponsor,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => <TableRowText variant="body2">{params?.row?.sponsor}</TableRowText>,
    },
    {
      field: 'Date',
      headerName: 'Creation Date',
      width: 150,
      editable: false,
      filterable: true,
      type: 'date',
      valueGetter: (params) => `${formatDateTime(params?.row?.createdAt, userCountry)}`,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row?.createdAt ? `${formatDate(params?.row?.createdAt, userCountry)} ` : '--'}{' '}
        </TableRowText>
      ),
    },
    {
      field: 'ProgramPlan',
      headerName: 'Program Plan',
      width: 150,
      editable: false,
      filterable: true,
      type: 'string',
      valueGetter: (params) => programMap[params?.row?.program],
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">{programMap[params?.row?.program] || '--'}</TableRowText>
      ),
    },
    {
      field: 'LastLogin',
      headerName: 'Last Login',
      width: 140,
      editable: false,
      filterable: true,
      type: 'date',
      valueGetter: (params) => params?.row?.lastLogin,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row.lastLogin ? computeLastLogin(params?.row.lastLogin) : '--'}
        </TableRowText>
      ),
    },
    {
      field: 'lastECG',
      headerName: 'Last ECG',
      width: 140,
      type: 'string',
      filterOperators: getDateFilterOperators(),
      valueGetter: (params) =>
        `${ params?.row?.lastRecordedEKG?.recordedAt ? formatDateTime(
          params?.row?.lastRecordedEKG?.recordedAt,
          userCountry,
        ) : '--'}  (${getEKGAlgorithmDeterminationDisplayText(
          params?.row?.lastRecordedEKG?.algorithmDetermination,
        )}) `,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row?.lastRecordedEKG?.recordedAt
            ? `${formatDate(
                params?.row?.lastRecordedEKG?.recordedAt,
                userCountry,
              )} (${getEKGAlgorithmDeterminationDisplayText(
                params?.row?.lastRecordedEKG?.algorithmDetermination,
              )}) `
            : '--'}{' '}
        </TableRowText>
      ),
    },
    {
      field: 'lastBP',
      headerName: 'Last BP',
      width: 140,
      filterOperators: getDateFilterOperators(),
      type: 'string',
      valueGetter: (params) =>
        `${params?.row?.lastRecordedBP?.recordedAt ? formatDateTime(params?.row?.lastRecordedBP?.recordedAt, userCountry) : '--'} (${
          params?.row?.lastRecordedBP?.systolic ? params?.row?.lastRecordedBP?.systolic : '--'
        }/${
          params?.row?.lastRecordedBP?.diastolic ? params?.row?.lastRecordedBP?.diastolic : '--'
        })`,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row?.lastRecordedBP?.recordedAt ? (
            <>
              {formatDate(params.row.lastRecordedBP.recordedAt, userCountry)}{' '}
              {params.row.lastRecordedBP
                ? `(${params.row.lastRecordedBP.systolic}/${params.row.lastRecordedBP.diastolic})`
                : ''}
            </>
          ) : (
            '--'
          )}
        </TableRowText>
      ),
    },
    {
      field: 'baselineBP',
      headerName: 'Baseline BP',
      width: 140,
      filterOperators: getDateFilterOperators(),
      type: 'string',
      valueGetter: (params) =>
        `${params?.row?.baselineBP?.updatedAt ? formatDateTime(params?.row?.baselineBP?.updatedAt, userCountry) : '--'} (${
          params?.row?.baselineBP?.systolic ? params?.row?.baselineBP?.systolic : '--'
        }/${params?.row?.baselineBP?.diastolic ? params?.row?.baselineBP?.diastolic : '--'})`,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row?.baselineBP?.updatedAt ? (
            <>
              {formatDate(params.row.baselineBP.updatedAt, userCountry)}{' '}
              {params.row.baselineBP
                ? `(${params.row.baselineBP.systolic}/${params.row.baselineBP.diastolic})`
                : ''}
            </>
          ) : (
            '--'
          )}
        </TableRowText>
      ),
    },
    {
      field: 'healthDetails',
      headerName: 'Health Details (HHA)',
      width: 150,
      editable: false,
      filterable: false,
      type: 'string',
      valueGetter: (params) => `${ params?.row?.hhaDate ? formatDate(params?.row?.hhaDate, userCountry) :'--'}` ,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row?.hhaDate ? `${formatDate(params?.row?.hhaDate, userCountry)} ` : '--'}
        </TableRowText>
      ),
    },
    {
      field: 'clinicianReviewCredits',
      headerName: 'Clinician Review Credits',
      width: 150,
      editable: false,
      filterable: false,
      type: 'string',
      valueGetter: (params) =>
        `${params?.row?.clinicianRemainingCredits} of ${params?.row?.clinicianTotalCredits} Credits left`,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {`${params?.row?.clinicianRemainingCredits} of ${params?.row?.clinicianTotalCredits} Credits left`}
        </TableRowText>
      ),
    },
    {
      field: 'nextsession',
      headerName: 'Next Session',
      width: 120,
      editable: false,
      filterable: true,
      type: 'string',
      valueGetter: (params) =>
        params?.row.nextSession > new Date()
          ? moment(params?.row.nextSession).format('MM-DD-YYYY')
          : 'Unscheduled',
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row.nextSession > new Date()
            ? moment(params?.row.nextSession).format('MM-DD-YYYY')
            : 'Unscheduled'}
        </TableRowText>
      ),
    },
    {
      field: 'cardiologySessions',
      headerName: 'Cardiology Sessions',
      width: 150,
      editable: false,
      filterable: false,
      type: 'string',
      valueGetter: (params) =>
        `${params?.row?.doctorRemainingCredits} of ${params?.row?.doctorTotalCredits} Remaining`,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {`${params?.row?.doctorRemainingCredits} of ${params?.row?.doctorTotalCredits} Remaining`}
        </TableRowText>
      ),
    },
    {
      field: 'phonenumber',
      headerName: 'Phone Number',
      width: 100,
      editable: false,
      filterable: false,
      type: 'string',
      valueGetter: (params) => params?.row?.ParticipantPhoneNumber,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">{params?.row?.ParticipantPhoneNumber}</TableRowText>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 150,
      editable: false,
      filterable: true,
      type: 'string',
      valueGetter: (params) => params?.row?.participantEmail,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">{params?.row?.participantEmail}</TableRowText>
      ),
    },
    {
      field: 'age',
      headerName: 'Age',
      width: 100,
      editable: false,
      filterable: true,
      type: 'number',
      valueGetter: (params) => getAge(params?.row?.participantDateOfBirth),
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {params?.row?.participantDateOfBirth ? getAge(params?.row?.participantDateOfBirth) : '--'}
        </TableRowText>
      ),
    },
    {
      field: 'dateofbirth',
      headerName: 'Date of Birth',
      width: 140,
      editable: false,
      filterable: true,
      type: 'date',
      valueGetter: (params) => formatDateTime(params?.row?.participantDateOfBirth, userCountry),

      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => (
        <TableRowText variant="body2">
          {formatDateTime(params?.row?.participantDateOfBirth, userCountry) || '--'}
        </TableRowText>
      ),
    },
    {
      field: 'Gender',
      headerName: 'Gender',
      width: 100,
      editable: false,
      filterable: true,
      type: 'singleSelect',
      valueOptions: GENDERS,
      valueGetter: (params) => params?.row?.gender,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => <TableRowText variant="body2">{params?.row?.gender}</TableRowText>,
    },
    {
      field: 'state',
      headerName: 'State',
      width: 100,
      editable: false,
      filterable: true,
      type: 'string',
      valueGetter: (params) => params?.row?.state,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => <TableRowText variant="body2">{params?.row?.state}</TableRowText>,
    },
    {
      field: 'zipCode',
      headerName: 'Zip Code',
      width: 100,
      editable: false,
      filterable: true,
      type: 'string',
      valueGetter: (params) => params?.row?.zipCode,
      valueFormatter: ({ value }) => `${value}`,
      renderCell: (params) => <TableRowText variant="body2">{params?.row?.zipCode}</TableRowText>,
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginLeft: '30px' }}>
        <BasicBreadcrumbs
          data={[
            {
              type: 'image',
              url: isAdmin
                ? COACH_ROUTES.ADMIN_TELEKARDIA_DASHBOARD
                : COACH_ROUTES.TELEKARDIA_DASHBOARD,
              src: 'Home',
            },
            { type: 'text', name: 'Member List' },
          ]}
        />
        <Box style={{ display: 'flex', columnGap: '16px', marginRight: '30px' }}>
          <Typography variant="body1" style={{ fontFamily: 'Work Sans' }}>
            {' '}
            Last Refreshed:{' '}
            <b>
              {'  '}
              {moment(refreshTime).format('hh:mm A')}
            </b>
          </Typography>

          <RefreshIcon onClick={() => dispatch(refreshMemberList())} />
        </Box>
      </Box>
      <SectionMain
        bodyElement={[
          {
            childElements: (
              <Grid container>
                <Grid item xs={12}>
                  <MuiTableComponent
                    // fileName={DOCTOR_CONSULTATION}
                    getRowId={(row) => row.participantId}
                    // loading={loading}
                    columns={columnData}
                    rows={filteredRow}
                    densityToolbar={true}
                    columnToolbar={true}
                    exportToolbar={isAdmin}
                    filterToolbar={true}
                    pagination
                    paginationMode="server"
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    rowsPerPageOptions={[20, 50, 100, totalRowCount]}
                    rowCount={totalRowCount}
                  />
                </Grid>
              </Grid>
            ),
          },
        ]}
      />
    </>
  );
}
